import { CalendarClock, Truck } from "lucide-react";
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { EditingBadge } from "../components/common/EditingBadge";
import { StatusBadge } from "../components/common/StatusBadge";
import { TablePagination } from "../components/common/TablePagination";
import { BULK_ASSIGNMENT_KEEP_VALUE, DispatchBoardBulkActions } from "../components/dispatch/DispatchBoardBulkActions";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useLocalBoardRows, useSyncChanges, useSyncPresenceVersion, useSyncStatus } from "../hooks/useSyncEngine";
import { extractErrorMessage, fetchDispatchTripDetail, fetchLookups, fetchResourceList, patchResource, updateResource } from "../lib/api";
import { loadBrowserCache, saveBrowserCache } from "../lib/browserCache";
import { loadIndexedCache, saveIndexedCache } from "../lib/indexedDbCache";
import { formatDateTime } from "../lib/dateTime";
import { clearSavedPageState, loadSavedPageState, savePageState } from "../lib/filterStorage";
import { normalizeBoardStatus } from "../lib/statuses";
import { queryBoardRows } from "../lib/sync/boardQuery";
import { syncEngine } from "../lib/sync/syncEngine";

const preloadDispatchBoardDetailSheet = () => import("../components/dispatch/DispatchBoardDetailSheet");
const DispatchBoardDetailSheet = lazy(() => preloadDispatchBoardDetailSheet().then((module) => ({ default: module.DispatchBoardDetailSheet })));
const DEFAULT_PAGE_SIZE = 30;
const DISPATCH_BOARD_FILTER_STORAGE_KEY = "dispatch-board-saved-filters-v1";

const getDefaultDispatchBoardPageState = () => ({
  search: "",
  filters: { customer_id: "", status: "", driver_id: "", truck_id: "", trailer_id: "", start_date: "", end_date: "", sort_by: "load_number", sort_dir: "asc" },
  pageSize: DEFAULT_PAGE_SIZE,
});

const DISPATCH_LOOKUPS_CACHE_KEY = "lumen-tms-dispatch-lookups-v1";
const dispatchDetailCacheKey = (tripId) => `lumen-tms-dispatch-detail-v1:${tripId}`;
const dispatchBoardRowsCacheKey = ({ filters, pageSize, page, search }) => `lumen-tms-dispatch-board-v1:${JSON.stringify({ search, filters, page, pageSize })}`;
const BROWSER_CACHE_TTL_MS = 30 * 60 * 1000;

const BULK_ASSIGNMENT_FIELDS = ["driver_id", "truck_id", "trailer_id"];

const defaultBulkAssignmentState = () => ({
  driver_id: BULK_ASSIGNMENT_KEEP_VALUE,
  truck_id: BULK_ASSIGNMENT_KEEP_VALUE,
  trailer_id: BULK_ASSIGNMENT_KEEP_VALUE,
});

const getInitialDispatchBoardPageState = () => {
  const state = loadSavedPageState(DISPATCH_BOARD_FILTER_STORAGE_KEY, getDefaultDispatchBoardPageState());
  return { ...state, filters: { ...state.filters, status: normalizeBoardStatus(state.filters?.status || "") } };
};

const formatDistance = (miles, unit = "Miles") => {
  if (miles == null) {
    return "Distance pending";
  }
  const displayValue = unit === "Kilometers" ? miles * 1.60934 : miles;
  return `${displayValue.toLocaleString("en-CA", { maximumFractionDigits: 1, minimumFractionDigits: 1 })} ${unit === "Kilometers" ? "km" : "mi"}`;
};
const sortIndicator = (active, direction) => (active ? (direction === "asc" ? "↑" : "↓") : "↕");

const SortHeaderButton = ({ label, sortKey, filters, onToggle, testId }) => (
  <button
    className={`inline-flex items-center gap-2 text-left text-xs font-semibold uppercase tracking-[0.14em] ${filters.sort_by === sortKey ? "text-zinc-900" : "text-zinc-500"}`}
    data-testid={testId}
    onClick={() => onToggle(sortKey)}
    type="button"
  >
    <span>{label}</span>
    <span aria-hidden="true">{sortIndicator(filters.sort_by === sortKey, filters.sort_dir)}</span>
  </button>
);

export const DispatchBoardPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") || "";
  const focusTripId = searchParams.get("trip_id") || "";
  const initialPageState = getInitialDispatchBoardPageState();
  const [boardRows, setBoardRows] = useState([]);
  const [totalBoardRows, setTotalBoardRows] = useState(0);
  const [lookups, setLookups] = useState({ customers: [], trucks: [], trailers: [], drivers: [], statuses: [], board_statuses: [], stop_types: [], currencies: [], distance_unit: "Miles" });
  const [filters, setFilters] = useState(initialPageState.filters);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [referenceDialogOpen, setReferenceDialogOpen] = useState(false);
  const [referenceSaving, setReferenceSaving] = useState(false);
  const [referenceDraft, setReferenceDraft] = useState("");
  const [referenceTargetRow, setReferenceTargetRow] = useState(null);
  const [autoOpenedKey, setAutoOpenedKey] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageState.pageSize || DEFAULT_PAGE_SIZE);
  const [selectedTripIds, setSelectedTripIds] = useState([]);
  const [bulkAssignmentDraft, setBulkAssignmentDraft] = useState(defaultBulkAssignmentState());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [cacheFallbackState, setCacheFallbackState] = useState({ lookups: false, board: false });
  const { rows: localBoardRows, ready: localBoardReady } = useLocalBoardRows();
  const syncStatus = useSyncStatus();
  useSyncPresenceVersion();
  const useLocalBoard = syncStatus.localFirst && localBoardReady && filters.status !== "Delivered" && filters.status !== "All";
  const localBoardPayload = useMemo(() => (useLocalBoard ? queryBoardRows(localBoardRows, { search, filters, page: currentPage, pageSize }) : null), [useLocalBoard, localBoardRows, search, filters, currentPage, pageSize]);
  const boardFilterStatuses = lookups.board_statuses.filter((statusValue) => statusValue !== "Delivered");
  const boardCacheKey = useMemo(() => dispatchBoardRowsCacheKey({ search, filters, page: currentPage, pageSize }), [currentPage, filters, pageSize, search]);
  const hasPendingBulkChanges = BULK_ASSIGNMENT_FIELDS.some((field) => bulkAssignmentDraft[field] !== BULK_ASSIGNMENT_KEEP_VALUE);
  const offlineLocalMessage = useLocalBoard && !syncStatus.online ? "You're offline — showing the dispatch board from local data. Changes you save will sync automatically when the connection returns." : "";
  const cacheFallbackMessage = offlineLocalMessage || (cacheFallbackState.board ? "Showing cached dispatch board data while the live board reconnects." : cacheFallbackState.lookups ? "Showing cached dispatch lookups while the live board reconnects." : "");
  const allVisibleSelected = Boolean(boardRows.length) && boardRows.every((row) => selectedTripIds.includes(row.id));

  const applyCachedBoardPayload = (payload) => {
    setBoardRows(payload?.items || []);
    setTotalBoardRows(payload?.total_items || 0);
  };

  const loadBoardRows = async ({ silent = false } = {}) => {
    if (localBoardPayload) {
      applyCachedBoardPayload(localBoardPayload);
      setCacheFallbackState((current) => ({ ...current, board: false }));
      setLoading(false);
      return;
    }
    if (!silent) {
      setLoading(true);
    }
    const cachedPayload = loadBrowserCache(boardCacheKey, BROWSER_CACHE_TTL_MS) || await loadIndexedCache(boardCacheKey, BROWSER_CACHE_TTL_MS);
    if (cachedPayload) {
      applyCachedBoardPayload(cachedPayload);
    }
    try {
      const rowsPayload = await fetchResourceList("/dispatch-board", { search, ...filters, page: currentPage, page_size: pageSize });
      applyCachedBoardPayload(rowsPayload);
      saveBrowserCache(boardCacheKey, rowsPayload);
      saveIndexedCache(boardCacheKey, rowsPayload);
      setCacheFallbackState((current) => ({ ...current, board: false }));
    } catch (error) {
      if (cachedPayload) {
        applyCachedBoardPayload(cachedPayload);
        setCacheFallbackState((current) => ({ ...current, board: true }));
        toast.warning("Showing cached dispatch board data while the live board reconnects.");
      } else {
        toast.error(extractErrorMessage(error, "Unable to load dispatch board."));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSharedLookups = async () => {
    const cachedLookups = loadBrowserCache(DISPATCH_LOOKUPS_CACHE_KEY, BROWSER_CACHE_TTL_MS);
    if (cachedLookups) {
      setLookups(cachedLookups);
    }
    const indexedLookups = cachedLookups ? null : await loadIndexedCache(DISPATCH_LOOKUPS_CACHE_KEY, BROWSER_CACHE_TTL_MS);
    if (indexedLookups) {
      setLookups(indexedLookups);
    }
    try {
      const lookupPayload = await fetchLookups();
      setLookups(lookupPayload);
      saveBrowserCache(DISPATCH_LOOKUPS_CACHE_KEY, lookupPayload);
      saveIndexedCache(DISPATCH_LOOKUPS_CACHE_KEY, lookupPayload);
      setCacheFallbackState((current) => ({ ...current, lookups: false }));
    } catch (error) {
      if (cachedLookups || indexedLookups) {
        setCacheFallbackState((current) => ({ ...current, lookups: true }));
        toast.warning("Showing cached dispatch lookups while the live board reconnects.");
      } else {
        toast.error(extractErrorMessage(error, "Unable to load dispatch filters."));
      }
    }
  };

  useEffect(() => {
    preloadDispatchBoardDetailSheet();
  }, []);

  useEffect(() => {
    if (detailOpen && selectedLoad?.id) {
      syncEngine.setEditing("load", selectedLoad.id);
    } else {
      syncEngine.clearEditing();
    }
  }, [detailOpen, selectedLoad?.id]);

  useEffect(() => () => syncEngine.clearEditing(), []);

  const searchRestoredRef = useRef(false);
  useEffect(() => {
    if (searchRestoredRef.current) {
      return;
    }
    searchRestoredRef.current = true;
    const savedSearch = initialPageState.search || "";
    if (searchParams.get("q") || searchParams.get("trip_id") || !savedSearch) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("q", savedSearch);
    setSearchParams(nextParams, { replace: true });
    // Restores a previously "Saved" search only once when the page is first opened —
    // must not re-run on every searchParams change, or clearing the search box would
    // immediately bring the saved value back since there'd be no "q" param momentarily.
  }, [initialPageState.search, searchParams, setSearchParams]);

  useEffect(() => {
    loadSharedLookups();
  }, []);

  useSyncChanges(["lookups"], () => loadSharedLookups());
  useSyncChanges(["board"], () => {
    if (!localBoardPayload) {
      loadBoardRows({ silent: true });
    }
  });

  useEffect(() => {
    loadBoardRows();
  }, [localBoardPayload, search, filters.customer_id, filters.status, filters.driver_id, filters.truck_id, filters.trailer_id, filters.start_date, filters.end_date, filters.sort_by, filters.sort_dir, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filters.customer_id, filters.status, filters.driver_id, filters.truck_id, filters.trailer_id, filters.start_date, filters.end_date, filters.sort_by, filters.sort_dir, pageSize]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(totalBoardRows / pageSize));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, pageSize, totalBoardRows]);

  useEffect(() => {
    setAutoOpenedKey("");
  }, [search, focusTripId]);

  useEffect(() => {
    setSelectedTripIds([]);
    setBulkAssignmentDraft(defaultBulkAssignmentState());
  }, [currentPage, pageSize, search, filters.customer_id, filters.status, filters.driver_id, filters.truck_id, filters.trailer_id, filters.start_date, filters.end_date, filters.sort_by, filters.sort_dir]);

  useEffect(() => {
    setSelectedTripIds((current) => current.filter((tripId) => boardRows.some((row) => row.id === tripId)));
  }, [boardRows]);

  useEffect(() => {
    const targetKey = `${search}:${focusTripId}`;
    if (loading || !boardRows.length || autoOpenedKey === targetKey) {
      return;
    }
    if (!search && !focusTripId) {
      return;
    }
    const targetRow = focusTripId ? boardRows.find((row) => row.id === focusTripId) : boardRows.find((row) => row.load_number === search);
    if (targetRow) {
      setAutoOpenedKey(targetKey);
      openDetailPanel(targetRow);
    }
  }, [loading, boardRows, search, focusTripId, autoOpenedKey]);

  const updateFilter = (event) => setFilters((current) => ({ ...current, [event.target.name]: event.target.value }));

  const toggleSort = (column) => {
    setFilters((current) => ({
      ...current,
      sort_by: column,
      sort_dir: current.sort_by === column && current.sort_dir === "asc" ? "desc" : "asc",
    }));
  };

  const handleSaveFilters = () => {
    savePageState(DISPATCH_BOARD_FILTER_STORAGE_KEY, { search, filters, pageSize });
    toast.success("Dispatch board filters saved for this browser.");
  };

  const handleResetSavedFilters = () => {
    const defaultState = getDefaultDispatchBoardPageState();
    clearSavedPageState(DISPATCH_BOARD_FILTER_STORAGE_KEY);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("q");
    setSearchParams(nextParams, { replace: true });
    setFilters(defaultState.filters);
    setPageSize(defaultState.pageSize);
    setCurrentPage(1);
    toast.success("Saved dispatch board filters cleared.");
  };

  const applyUpdatedCustomerReference = async (updatedLoad) => {
    setBoardRows((current) => current.map((row) => (row.load_id === updatedLoad.id ? { ...row, customer_reference_number: updatedLoad.customer_reference_number } : row)));
    if (selectedLoad?.id === updatedLoad.id) {
      setSelectedLoad(updatedLoad);
    }
    if (!useLocalBoard) {
      await loadBoardRows({ silent: true });
    }
  };

  const openCustomerReferenceDialog = (row) => {
    setReferenceTargetRow(row);
    setReferenceDraft(row.customer_reference_number || "");
    setReferenceDialogOpen(true);
  };

  const handleReferenceDialogOpenChange = (nextOpen) => {
    setReferenceDialogOpen(nextOpen);
    if (!nextOpen) {
      setReferenceTargetRow(null);
      setReferenceDraft("");
    }
  };

  const handleCustomerReferenceSave = async () => {
    if (!referenceTargetRow) {
      return;
    }
    try {
      setReferenceSaving(true);
      const updatedLoad = await patchResource("/loads", `${referenceTargetRow.load_id}/customer-reference`, { customer_reference_number: referenceDraft });
      await applyUpdatedCustomerReference(updatedLoad);
      toast.success("Customer reference updated.");
      handleReferenceDialogOpenChange(false);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to update customer reference."));
    } finally {
      setReferenceSaving(false);
    }
  };

  const toggleTripSelection = (tripId) => {
    setSelectedTripIds((current) => current.includes(tripId) ? current.filter((item) => item !== tripId) : [...current, tripId]);
  };

  const toggleSelectAllVisible = () => {
    setSelectedTripIds(allVisibleSelected ? [] : boardRows.map((row) => row.id));
  };

  const handleBulkDraftChange = (field, value) => {
    setBulkAssignmentDraft((current) => ({ ...current, [field]: value }));
  };

  const handleBulkAssign = async () => {
    const applyFields = BULK_ASSIGNMENT_FIELDS.filter((field) => bulkAssignmentDraft[field] !== BULK_ASSIGNMENT_KEEP_VALUE);
    if (!selectedTripIds.length || !applyFields.length) {
      return;
    }
    try {
      setBulkSaving(true);
      await updateResource("/dispatch-board/bulk-assign", null, {
        trip_ids: selectedTripIds,
        apply_fields: applyFields,
        driver_id: applyFields.includes("driver_id") ? bulkAssignmentDraft.driver_id : null,
        truck_id: applyFields.includes("truck_id") ? bulkAssignmentDraft.truck_id : null,
        trailer_id: applyFields.includes("trailer_id") ? bulkAssignmentDraft.trailer_id : null,
      });
      toast.success(`Updated ${selectedTripIds.length} dispatch trip${selectedTripIds.length === 1 ? "" : "s"}.`);
      setSelectedTripIds([]);
      setBulkAssignmentDraft(defaultBulkAssignmentState());
      if (!useLocalBoard) {
        await loadBoardRows({ silent: true });
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to apply bulk assignments."));
    } finally {
      setBulkSaving(false);
    }
  };

  const openDetailPanel = async (row) => {
    const cachedDetail = loadBrowserCache(dispatchDetailCacheKey(row.id), BROWSER_CACHE_TTL_MS);
    if (cachedDetail) {
      setSelectedLoad(cachedDetail);
      setSelectedTripId(row.id);
      setDetailOpen(true);
      preloadDispatchBoardDetailSheet();
      fetchDispatchTripDetail(row.id)
        .then((parentLoad) => {
          setSelectedLoad(parentLoad || null);
          saveBrowserCache(dispatchDetailCacheKey(row.id), parentLoad || null);
        })
        .catch(() => null);
      return;
    }
    try {
      const [, parentLoad] = await Promise.all([preloadDispatchBoardDetailSheet(), fetchDispatchTripDetail(row.id)]);
      setSelectedLoad(parentLoad || null);
      setSelectedTripId(row.id);
      setDetailOpen(true);
      saveBrowserCache(dispatchDetailCacheKey(row.id), parentLoad || null);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to open this dispatch trip."));
    }
  };

  const refreshSelectedLoad = async () => {
    if (!selectedTripId) {
      if (!useLocalBoard) {
        await loadBoardRows({ silent: true });
      }
      return;
    }
    const parentLoad = await fetchDispatchTripDetail(selectedTripId);
    setSelectedLoad(parentLoad || null);
    saveBrowserCache(dispatchDetailCacheKey(selectedTripId), parentLoad || null);
    if (!useLocalBoard) {
      await loadBoardRows({ silent: true });
    }
  };

  const handleTripDeleted = async () => {
    handleDetailOpenChange(false);
    if (!useLocalBoard) {
      await loadBoardRows({ silent: true });
    }
  };

  const handleDetailOpenChange = (nextOpen) => {
    setDetailOpen(nextOpen);
    if (!nextOpen) {
      setSelectedLoad(null);
      setSelectedTripId(null);
    }
  };

  const handleDetailSave = async (payload, inlineError) => {
    if (inlineError) {
      toast.error(inlineError);
      return;
    }

    try {
      setSaving(true);
      const writeResult = await syncEngine.write({ method: "put", url: `/loads/${selectedLoad.id}`, data: payload, label: `Save load ${selectedLoad.load_number}`, entityType: "load", entityId: selectedLoad.id, headers: selectedLoad.updated_at ? { "X-Expected-Updated-At": selectedLoad.updated_at } : undefined });
      if (writeResult.queued) {
        toast.info("You're offline — this change is saved on this device and will sync automatically when the connection returns.");
        handleDetailOpenChange(false);
        return;
      }
      const updatedLoad = writeResult.data;
      if (updatedLoad.conflict) {
        toast.warning(`${updatedLoad.conflict.other_user_name || "Another dispatcher"} also changed this load. ${updatedLoad.conflict.summary}`, { duration: 9000 });
      }
      if (updatedLoad.distance_alerts?.length) {
        toast.warning(updatedLoad.distance_alerts[0]);
      }
      if (selectedTripId) {
        saveBrowserCache(dispatchDetailCacheKey(selectedTripId), updatedLoad);
      }
      const finalDispatchStatus = updatedLoad.dispatch_status || updatedLoad.status;
      const movedToInvoices = ["Delivered", "Invoiced"].includes(finalDispatchStatus);
      toast.success(movedToInvoices ? "Load saved and moved out of the board." : "Dispatch board updated.");
      handleDetailOpenChange(false);
      if (!useLocalBoard) {
        loadBoardRows({ silent: true });
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to save this load."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dispatch-board-page">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Operational dispatch board</p>
          <h1 className="mt-2 text-4xl font-semibold text-zinc-900">Dispatch Board</h1>
        </div>
        <p className="text-xs font-medium text-zinc-500" data-testid="dispatch-board-data-source">{useLocalBoard ? "Reading from local data · live sync on" : "Reading from server"}</p>
      </section>

      <section className="panel-surface grid gap-4 px-5 py-5 md:grid-cols-2 xl:grid-cols-4" data-testid="dispatch-board-filters-panel">
        <select className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="dispatch-board-filter-customer" name="customer_id" onChange={updateFilter} value={filters.customer_id}><option value="">All customers</option>{lookups.customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.customer_name}</option>)}</select>
          <select className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="dispatch-board-filter-status" name="status" onChange={updateFilter} value={filters.status}><option value="">All active statuses</option>{boardFilterStatuses.map((statusValue) => <option key={statusValue} value={statusValue}>{statusValue}</option>)}<option value="Delivered">Delivered</option><option value="All">All statuses (incl. Delivered)</option></select>
        <select className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="dispatch-board-filter-driver" name="driver_id" onChange={updateFilter} value={filters.driver_id}><option value="">All drivers</option>{lookups.drivers.map((driver) => <option key={driver.id} value={driver.id}>{driver.full_name}</option>)}</select>
        <select className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="dispatch-board-filter-truck" name="truck_id" onChange={updateFilter} value={filters.truck_id}><option value="">All trucks</option>{lookups.trucks.map((truck) => <option key={truck.id} value={truck.id}>{truck.truck_number}</option>)}</select>
        <select className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="dispatch-board-filter-trailer" name="trailer_id" onChange={updateFilter} value={filters.trailer_id}><option value="">All trailers</option>{lookups.trailers.map((trailer) => <option key={trailer.id} value={trailer.id}>{trailer.trailer_number}</option>)}</select>
        <input className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="dispatch-board-filter-start-date" name="start_date" onChange={updateFilter} type="date" value={filters.start_date} />
        <input className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="dispatch-board-filter-end-date" name="end_date" onChange={updateFilter} type="date" value={filters.end_date} />
        <div className="grid grid-cols-2 gap-3">
          <select className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="dispatch-board-sort-by" name="sort_by" onChange={updateFilter} value={filters.sort_by}><option value="load_number">Trip #</option><option value="customer">Customer</option><option value="pickup_datetime">Planned picked</option><option value="delivery_datetime">Planned delivered</option><option value="status">Status</option></select>
          <select className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="dispatch-board-sort-direction" name="sort_dir" onChange={updateFilter} value={filters.sort_dir}><option value="asc">Asc</option><option value="desc">Desc</option></select>
        </div>
        <div className="flex flex-wrap items-center gap-3 md:col-span-2 xl:col-span-4" data-testid="dispatch-board-saved-filter-actions">
          <button className="rounded-2xl bg-zinc-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-zinc-800" data-testid="dispatch-board-save-filters-button" onClick={handleSaveFilters} type="button">Save filters</button>
          <button className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900" data-testid="dispatch-board-reset-saved-filters-button" onClick={handleResetSavedFilters} type="button">Reset saved filters</button>
        </div>
      </section>

      {cacheFallbackMessage ? <section className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="dispatch-board-cache-status">{cacheFallbackMessage}</section> : null}

      <DispatchBoardBulkActions
        applying={bulkSaving}
        hasPendingChanges={hasPendingBulkChanges}
        lookups={lookups}
        onApply={handleBulkAssign}
        onClearSelection={() => setSelectedTripIds([])}
        onValueChange={handleBulkDraftChange}
        selectedCount={selectedTripIds.length}
        values={bulkAssignmentDraft}
      />

      <section className="table-shell" data-testid="dispatch-board-table-section">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="dispatch-board-table">
            <thead>
              <tr>
                <th>
                  <input checked={allVisibleSelected} className="h-4 w-4 rounded border-zinc-300" data-testid="dispatch-board-select-all-visible" onChange={toggleSelectAllVisible} type="checkbox" />
                </th>
                <th><SortHeaderButton filters={filters} label="Trip #" onToggle={toggleSort} sortKey="load_number" testId="dispatch-board-sort-header-load_number" /></th>
                <th><SortHeaderButton filters={filters} label="Customer" onToggle={toggleSort} sortKey="customer" testId="dispatch-board-sort-header-customer" /></th>
                <th>Route</th>
                <th>Driver / Truck / Trailer</th>
                <th>Distance</th>
                <th><SortHeaderButton filters={filters} label="Planned picked" onToggle={toggleSort} sortKey="pickup_datetime" testId="dispatch-board-sort-header-picked" /></th>
                <th><SortHeaderButton filters={filters} label="Planned delivered" onToggle={toggleSort} sortKey="delivery_datetime" testId="dispatch-board-sort-header-delivered" /></th>
                <th><SortHeaderButton filters={filters} label="Status" onToggle={toggleSort} sortKey="status" testId="dispatch-board-sort-header-status" /></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="py-10 text-center text-sm text-zinc-500" colSpan={9} data-testid="dispatch-board-loading-state">Loading dispatch board...</td>
                </tr>
              ) : totalBoardRows ? (
                boardRows.map((row) => (
                  <tr className="cursor-pointer transition hover:bg-zinc-50" data-testid={`dispatch-board-row-${row.id}`} key={row.id} onClick={() => openDetailPanel(row)}>
                    <td>
                      <input checked={selectedTripIds.includes(row.id)} className="h-4 w-4 rounded border-zinc-300" data-testid={`dispatch-board-row-select-${row.id}`} onChange={() => toggleTripSelection(row.id)} onClick={(event) => event.stopPropagation()} type="checkbox" />
                    </td>
                    <td>
                      <div className="text-left" data-testid={`dispatch-board-open-detail-${row.id}`}>
                        <p className="font-semibold text-zinc-900">{row.trip_number}</p>
                        <div className="flex flex-wrap gap-1">
                          {syncStatus.pendingEntityIds.has(row.load_id) ? <span className="mt-1 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-800" data-testid={`dispatch-board-pending-sync-${row.id}`}>Pending sync</span> : null}
                          <EditingBadge className="mt-1" editors={syncEngine.getEditors("load", row.load_id)} testId={`dispatch-board-editing-${row.id}`} />
                        </div>
                        <p className="mt-1 text-xs text-zinc-500">
                          Load {row.load_number} • {" "}
                          <button className="font-semibold text-zinc-700 underline decoration-zinc-300 underline-offset-2 transition hover:text-zinc-900" data-testid={`dispatch-board-customer-reference-trigger-${row.id}`} onClick={(event) => { event.stopPropagation(); openCustomerReferenceDialog(row); }} type="button">
                            Ref {row.customer_reference_number}
                          </button>
                        </p>
                      </div>
                    </td>
                    <td>
                      <p className="font-medium text-zinc-900">{row.customer_name}</p>
                      <p className="mt-1 text-xs text-zinc-500">Stops {row.start_stop_order} → {row.end_stop_order} • {row.stop_count} segment stops</p>
                      <p className="mt-1 text-xs text-zinc-500" data-testid={`dispatch-board-linked-load-${row.id}`}>{row.linked_load ? `Linked load ${row.linked_load.load_number}` : "No linked load"}</p>
                    </td>
                    <td>
                      <div className="flex items-start gap-2 text-sm text-zinc-700">
                        <Truck className="mt-0.5 h-4 w-4 text-zinc-400" />
                        <div>
                          <p>{row.pickup_city} → {row.delivery_city}</p>
                          <p className="mt-1 text-xs text-zinc-500">{row.pickup_company} to {row.delivery_company}</p>
                          <p className="mt-1 text-xs text-zinc-500">{row.shipment?.shipment_id || row.shipment_id || "—"} • {row.shipment?.shipment_direction || row.shipment_direction || "—"}</p>
                          <p className="mt-1 text-xs text-zinc-400" data-testid={`dispatch-board-route-scope-${row.id}`}>Trip segment {row.start_stop_order} → {row.end_stop_order}</p>
                        </div>
                      </div>
                    </td>
                    <td data-testid={`dispatch-board-assignment-${row.id}`}>
                      <p className="text-sm font-medium text-zinc-900">{row.combined_assignment}</p>
                      {row.is_power_only ? <span className="mt-1 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-indigo-700" data-testid={`dispatch-board-power-only-badge-${row.id}`}>Power only</span> : null}
                      {row.assignment_change_count > 1 ? <p className="mt-1 text-xs text-zinc-500" data-testid={`dispatch-board-assignment-count-${row.id}`}>{row.assignment_change_count} route assignments</p> : null}
                    </td>
                    <td data-testid={`dispatch-board-distance-${row.id}`}>{formatDistance(row.distance_miles, lookups.distance_unit)}</td>
                    <td>
                      <div className="flex items-start gap-2 text-sm text-zinc-700">
                        <CalendarClock className="mt-0.5 h-4 w-4 text-zinc-400" />
                        <span data-testid={`dispatch-board-picked-time-${row.id}`}>{formatDateTime(row.planned_picked_at)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-start gap-2 text-sm text-zinc-700">
                        <CalendarClock className="mt-0.5 h-4 w-4 text-zinc-400" />
                        <span data-testid={`dispatch-board-delivered-time-${row.id}`}>{formatDateTime(row.planned_delivered_at)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="space-y-2" data-testid={`dispatch-board-row-status-readonly-${row.id}`}>
                        <StatusBadge status={row.dispatch_status} testId={`dispatch-board-status-badge-${row.id}`} />
                        <p className="text-xs font-medium text-zinc-500">Status updates automatically from assignment and completed stops.</p>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="py-10 text-center text-sm text-zinc-500" colSpan={9} data-testid="dispatch-board-empty-state">No active loads match the current dispatch filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination currentPage={currentPage} pageSize={pageSize} setCurrentPage={setCurrentPage} setPageSize={setPageSize} testIdPrefix="dispatch-board-table" totalItems={totalBoardRows} />
      </section>

      <Suspense fallback={null}>
        <DispatchBoardDetailSheet
          activeTripId={selectedTripId}
          load={selectedLoad}
          lookups={lookups}
          onOpenChange={handleDetailOpenChange}
          onCustomerReferenceUpdated={applyUpdatedCustomerReference}
          onSave={handleDetailSave}
          onTripDeleted={handleTripDeleted}
          onTripLinkChanged={refreshSelectedLoad}
          open={detailOpen}
          saving={saving}
        />
      </Suspense>

      <Dialog onOpenChange={handleReferenceDialogOpenChange} open={referenceDialogOpen}>
        <DialogContent className="max-w-md rounded-[1.5rem] p-6" data-testid="dispatch-board-reference-dialog">
          <DialogHeader>
            <DialogTitle data-testid="dispatch-board-reference-dialog-title">Update customer reference</DialogTitle>
          </DialogHeader>

          {referenceTargetRow ? (
            <div className="space-y-5">
              <div className="grid gap-3 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-4 text-sm text-zinc-700" data-testid="dispatch-board-reference-dialog-context">
                <p data-testid="dispatch-board-reference-dialog-load-number"><span className="font-semibold text-zinc-900">Load #:</span> {referenceTargetRow.load_number}</p>
                <p data-testid="dispatch-board-reference-dialog-customer"><span className="font-semibold text-zinc-900">Customer:</span> {referenceTargetRow.customer_name}</p>
                <p data-testid="dispatch-board-reference-dialog-pickup"><span className="font-semibold text-zinc-900">Pickup:</span> {referenceTargetRow.pickup_company} — {referenceTargetRow.pickup_city}</p>
                <p data-testid="dispatch-board-reference-dialog-delivery"><span className="font-semibold text-zinc-900">Delivery:</span> {referenceTargetRow.delivery_company} — {referenceTargetRow.delivery_city}</p>
              </div>

              <label className="block space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Customer Ref #</span>
                <input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="dispatch-board-reference-dialog-input" onChange={(event) => setReferenceDraft(event.target.value)} type="text" value={referenceDraft} />
              </label>

              <div className="flex justify-end gap-3">
                <button className="rounded-2xl border border-zinc-200 px-4 py-3 text-sm font-semibold text-zinc-600 transition hover:border-zinc-900 hover:text-zinc-900" data-testid="dispatch-board-reference-dialog-cancel" onClick={() => handleReferenceDialogOpenChange(false)} type="button">Cancel</button>
                <button className="rounded-2xl bg-zinc-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-70" data-testid="dispatch-board-reference-dialog-save" disabled={referenceSaving || !referenceDraft.trim()} onClick={handleCustomerReferenceSave} type="button">{referenceSaving ? "Saving..." : "Save reference"}</button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};